import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Lock, AlertTriangle } from 'lucide-react';
import { useI18n } from '../contexts/LanguageContext';

interface ChangePinDialogProps {
  open: boolean;
  userId: string;
  isFirstLogin: boolean;
  onSuccess: () => void;
  onCancel?: () => void;
}

function isValidPin(pin: string): { valid: boolean; error?: string } {
  // Check minimum 6 digits
  if (pin.length < 6) {
    return { valid: false, error: 'PIN en az 6 hane olmalıdır' };
  }
  
  // Check for 4 same consecutive digits (1111, 2222, etc.)
  const samePattern = /(.)\1{3}/;
  if (samePattern.test(pin)) {
    return { valid: false, error: 'Aynı rakam 4 kez ardışık olamaz (örn: 1111)' };
  }
  
  // Check for 4 consecutive ascending digits (1234, 2345, etc.)
  for (let i = 0; i <= pin.length - 4; i++) {
    const num = parseInt(pin.substring(i, i + 4));
    const digits = pin.substring(i, i + 4).split('').map(Number);
    const isConsecutive = digits.every((d, idx) => idx === 0 || d === digits[idx - 1] + 1);
    if (isConsecutive) {
      return { valid: false, error: 'Ardışık 4 rakam kullanılamaz (örn: 1234)' };
    }
  }
  
  // Check for 4 consecutive descending digits (4321, 5432, etc.)
  for (let i = 0; i <= pin.length - 4; i++) {
    const digits = pin.substring(i, i + 4).split('').map(Number);
    const isConsecutive = digits.every((d, idx) => idx === 0 || d === digits[idx - 1] - 1);
    if (isConsecutive) {
      return { valid: false, error: 'Ardışık 4 rakam kullanılamaz (örn: 4321)' };
    }
  }
  
  return { valid: true };
}

export function ChangePinDialog({ 
  open, 
  userId, 
  isFirstLogin, 
  onSuccess,
  onCancel 
}: ChangePinDialogProps) {
  const { t, language } = useI18n();
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate new PIN with security rules
    const validation = isValidPin(newPin);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Check if new PIN and confirm PIN match
    if (newPin !== confirmPin) {
      toast.error(
        language === 'tr' 
          ? 'Yeni PIN\'ler eşleşmiyor' 
          : language === 'es'
          ? 'Los nuevos PINs no coinciden'
          : 'New PINs do not match'
      );
      return;
    }

    setLoading(true);

    try {
      const tenantId = localStorage.getItem('tenantId');
      const publicAnonKey = (await import('/utils/supabase/info')).publicAnonKey;
      const projectId = (await import('/utils/supabase/info')).projectId;

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-df8bdc4f/pg/users/${userId}/change-pin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}`,
            'X-Tenant-ID': tenantId || '',
          },
          body: JSON.stringify({ oldPin, newPin }),
        }
      );

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Non-JSON response from server:', text.substring(0, 200));
        throw new Error('Server returned invalid response. Please try again.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'PIN değiştirilemedi');
      }

      toast.success(
        language === 'tr' 
          ? '✅ PIN başarıyla değiştirildi!' 
          : language === 'es'
          ? '✅ ¡PIN cambiado exitosamente!'
          : '✅ PIN changed successfully!'
      );

      // Reset form
      setOldPin('');
      setNewPin('');
      setConfirmPin('');

      onSuccess();
    } catch (error: any) {
      console.error('PIN change error:', error);
      toast.error(error.message || 'PIN değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={!isFirstLogin ? onCancel : undefined}>
      <DialogContent className="sm:max-w-md" hideClose={isFirstLogin}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Lock className="w-6 h-6 text-blue-600" />
            {isFirstLogin 
              ? (language === 'tr' 
                  ? '🔒 İlk Giriş - PIN Değiştirin' 
                  : language === 'es'
                  ? '🔒 Primer Inicio - Cambiar PIN'
                  : '🔒 First Login - Change PIN')
              : (language === 'tr' 
                  ? 'PIN Değiştir' 
                  : language === 'es'
                  ? 'Cambiar PIN'
                  : 'Change PIN')
            }
          </DialogTitle>
        </DialogHeader>

        {isFirstLogin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">
                  {language === 'tr' 
                    ? 'Güvenlik Uyarısı' 
                    : language === 'es'
                    ? 'Aviso de Seguridad'
                    : 'Security Notice'}
                </p>
                <p>
                  {language === 'tr' 
                    ? 'Bu ilk girişiniz. Güvenliğiniz için lütfen PIN kodunuzu değiştirin.' 
                    : language === 'es'
                    ? 'Este es su primer inicio de sesión. Por favor cambie su PIN por seguridad.'
                    : 'This is your first login. Please change your PIN for security.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">
              {language === 'tr' ? 'Mevcut PIN' : t('currentPin') || 'Current PIN'}
            </label>
            <Input
              type="password"
              value={oldPin}
              onChange={(e) => setOldPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={language === 'tr' ? '6 haneli' : t('pinDigits') || '6 digits'}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
              required
              autoFocus
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-2">
              {language === 'tr' ? 'Yeni PIN' : t('newPin') || 'New PIN'}
            </label>
            <Input
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={language === 'tr' ? '6 haneli' : t('pinDigits') || '6 digits'}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-2">
              {language === 'tr' ? 'PIN Onayla' : t('confirmPin') || 'Confirm PIN'}
            </label>
            <Input
              type="password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={language === 'tr' ? '6 haneli' : t('pinDigits') || '6 digits'}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
              required
              disabled={loading}
            />
          </div>

          <div className="flex gap-2 pt-4">
            {!isFirstLogin && onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading}
                className="flex-1"
              >
                {language === 'tr' ? 'İptal' : t('cancel') || 'Cancel'}
              </Button>
            )}
            <Button
              type="submit"
              disabled={loading || !oldPin || !newPin || !confirmPin}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (language === 'tr' ? 'Değiştiriliyor...' : t('changingPin') || 'Changing PIN...') : (language === 'tr' ? 'PIN Değiştir' : t('changePin') || 'Change PIN')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}